import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SearchResultViewModel } from '../models/search-view.model';
import { Product } from '../../../shared/models/product.model';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { ItemTypes } from '../../../lists/shared/list-detail-management/model/list-detail-management-view.model';
import { SearchAnalyticsService } from '../../services/search-analytics.service';
import { PanAppState } from '@panamax/app-state';
import {
  EllipsisClickData,
  ProductClickData,
} from '../../../shared/models/usf-product-card.model';
import { SearchProductCardService } from '../../../shared/services/product-card/search-product-card.service';
import { PowerReviewProductState } from '@usf/ngrx-product/lib/models/power-review-product-state.model';
import { PowerReviewRollupModel } from '@usf/ngrx-product/lib/models/power-review-product.model';
import { UserService } from '@app/user/services';
import { Observable } from 'rxjs';
import { SubstitutionRowTabletComponent } from '@order/components/order-exceptions/substitution-row/tablet/substitution-row-tablet/substitution-row-tablet.component';
import { SubstitutionTypeEnum } from '@shared/constants/substitution-type-enum';
import { CoveoService } from '@shared/services/coveo/coveo.service';

@Component({
  selector: 'app-selectable-search-result',
  templateUrl: './selectable-search-result.component.html',
  styleUrls: ['./selectable-search-result.component.scss'],
})
export class SelectableSearchResultComponent {
  coveoService = inject(CoveoService);
  @Input() pwrRatingStarsMap: Map<string, PowerReviewRollupModel>;
  @Input() mode: UsfProductCardModeEnum;
  @Input() product: SearchResultViewModel;
  @Input() replacementProduct: SearchResultViewModel;
  @Input() isTouchExperience: boolean;
  @Input() isHideQuantity: boolean;
  @Input() pageType: string;
  @Input() totalNumberOfRecords: number;
  @Input() selectable = false;
  @Input() index: number;
  @Input() substitutionType: SubstitutionTypeEnum;
  @Input() overrideModeCheck = false;
  @Output() selectionChange = new EventEmitter<Product>();
  @Output() cardClicked = new EventEmitter<number>();
  productCardModes = UsfProductCardModeEnum;

  itemTypes = ItemTypes;
  isGuestUser$: Observable<boolean>;

  constructor(
    readonly panAppState: PanAppState,
    private searchAnalyticsService: SearchAnalyticsService,
    private searchProductCardService: SearchProductCardService,
    private userService: UserService,
  ) {
    this.isGuestUser$ = this.userService.isGuestUser$();
  }

  addHideQuantityToProduct() {
    return {
      ...this.product,
      hideQuantityBoxes: this.isHideQuantity,
    };
  }

  getPowerReviewRollupModel() {
    return this.pwrRatingStarsMap?.get(this.product?.productNumber?.toString());
  }

  getAlternativePowerReviewRollupModel() {
    return this.pwrRatingStarsMap?.get(
      this.product?.alternative?.product?.productNumber?.toString(),
    );
  }

  handleSelectionChange(value) {
    this.product.selected = value;
    this.selectionChange.emit(this.product);
  }

  quantityInputsDisabled(product: Product): boolean {
    return (
      !product?.orderable ||
      this.replacementProduct?.productNumber === this.product.productNumber
    );
  }

  async changeQuantityHandler(event: any) {
    const quantityUpdate = event.quantityUpdate;
    this.searchAnalyticsService.trackAndChangeQuantity(
      quantityUpdate,
      this.product,
      this.pageType,
      this.totalNumberOfRecords,
      this.mode,
      this.replacementProduct,
    );
  }

  openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    const buttons =
      this.searchProductCardService.constructEllipsisButtons(ellipsisClickData);
    this.searchProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  get itemType(): typeof ItemTypes {
    return ItemTypes;
  }

  async doProductCardClick(productClickData: ProductClickData) {
    let price =
      this.product?.pricing?.unitPrice ?? this.product?.pricing?.eachPrice;
    this.coveoService.onSearchResultClickEvent(
      this.product?.productNumber,
      price,
    );
    this.cardClicked.emit(this.index);
    this.searchAnalyticsService.trackSearchResultClick(this.product);
    this.searchProductCardService.routeToPDPFromSearch(
      productClickData.productNumber,
      productClickData.isQuickView,
      true,
    );
  }

  clickPromotions(event: EllipsisClickData) {
    this.searchProductCardService.openQuickViewModal(event, 'productStatus');
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.searchProductCardService.showProductStatuses(ellipsisClickData);
  }
}
